import { forwardRef, cloneElement, isValidElement } from "react";
import { Fade, Tooltip } from "@mui/material";
import { IHint } from "./Hint.props";
import { useTranslate } from "@/hooks";
import useLocalStorageState from "use-local-storage-state";
import { ReactMarkdown } from "@/ui";

const Hint = forwardRef<HTMLElement, IHint>(
	(
		{
			hint,
			hintActive = false,
			position = "top",
			width = "auto",
			children,
			textAlign = "left",
			textOverflow = false,
			blockHeight,
			spanStyle,
			offset = [0, 8],
			disableFlip = false,
			disableOverflow = false,
			alwaysOpen,
			...props
		},
		ref
	) => {
		const [storeEnabled] = useLocalStorageState("hintModeEnabled");
		const translate = useTranslate();
		const enabled = hintActive ? hintActive : storeEnabled;

		if (!enabled || !hint) {
			if (isValidElement(children)) {
				return cloneElement(children as React.ReactElement, { ref });
			}
			return <>{children}</>;
		}

		return (
			<Tooltip
				open={alwaysOpen ? true : undefined}
				TransitionComponent={Fade}
				TransitionProps={{ timeout: 0 }}
				placement={position}
				arrow
				title={
					<ReactMarkdown
						components={{
							p: ({ node, ...props }) => <p style={{ marginBottom: "6px" }} {...props} />,
							ol: ({ node, ...props }) => <ol style={{ padding: "0 0 0 16px" }} {...props} />,
						}}
					>
						{translate(hint)}
					</ReactMarkdown>
				}
				slotProps={{
					popper: {
						modifiers: [
							{
								name: "offset",
								options: { offset },
							},
							{
								name: "flip",
								enabled: !disableFlip,
							},
							{
								name: "preventOverflow",
								enabled: !disableOverflow,
								options: {
									padding: 16,
									altAxis: true,
								},
							},
						],
					},
					tooltip: {
						onClick: (e: any) => e.stopPropagation(),
						onMouseDown: (e: any) => e.stopPropagation(),
						sx: {
							background: "#F8FAFF",
							color: "#918c8c",
							padding: "8px 8px",
							borderRadius: "6px",
							boxSizing: "border-box",
							maxHeight: "90vh",
							overflowY: "auto",
							overflowX: "hidden",
							border: "1px solid #D9E2F0",
							fontSize: "11px",
							lineHeight: 1.35,
							maxWidth: "260px",
							boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
							whiteSpace: "normal",
							textAlign,
						},
					},
					arrow: {
						sx: {
							color: "#F8FAFF",
							"&:before": {
								border: "1px solid #D9E2F0",
							},
						},
					},
				}}
				{...props}
			>
				<span
					ref={ref}
					style={{
						display: "block",
						width,
						overflow: textOverflow ? "hidden" : "visible",
						height: blockHeight ?? "auto",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						...spanStyle,
					}}
				>
					{children}
				</span>
			</Tooltip>
		);
	}
);

export default Hint;
